import {
  DataRangeFilterEnum,
  type DateRangeFilterOption,
} from '@/constants/dates';
import dayjs from '@/config/dayjs';
import type { VaultFileAccount } from '@/store/accountsApi';
import { statementsVaultDateFilters } from '@/modules/document/grids/documentVault/statementsVaultGridConfig';

export const STATEMENT_VAULT_CATEGORY_ID = 1;
export const TAX_VAULT_CATEGORY_ID = 2;
export const FEE_STATEMENT_CATEGORY_ID = 3;
export const PERFORMANCE_STATEMENT_CATEGORY_ID = 4;

export function statementCategoryIds(showFeeAndPerformance: boolean): number[] {
  if (!showFeeAndPerformance) {
    return [STATEMENT_VAULT_CATEGORY_ID];
  }
  return [
    STATEMENT_VAULT_CATEGORY_ID,
    FEE_STATEMENT_CATEGORY_ID,
    PERFORMANCE_STATEMENT_CATEGORY_ID,
  ];
}

export function mergeFeedDocuments<T extends { VaultFile2Id: number }>(
  groups: readonly (readonly T[])[],
): T[] {
  const seen = new Set<number>();
  const merged: T[] = [];
  for (const group of groups) {
    for (const row of group) {
      if (seen.has(row.VaultFile2Id)) {
        continue;
      }
      seen.add(row.VaultFile2Id);
      merged.push(row);
    }
  }
  return merged;
}

export function unionVaultAccounts(
  groups: readonly (readonly VaultFileAccount[])[],
): VaultFileAccount[] {
  const seen = new Set<number>();
  const merged: VaultFileAccount[] = [];
  for (const group of groups) {
    for (const account of group) {
      if (seen.has(account.WealthAccountId)) {
        continue;
      }
      seen.add(account.WealthAccountId);
      merged.push(account);
    }
  }
  return merged;
}

const ADVISOR_FEED_DATE_FILTER_META: Partial<
  Record<DataRangeFilterEnum, { label: string; translationKey: string }>
> = {
  [DataRangeFilterEnum.Trailing1Month]: {
    label: 'Last Month',
    translationKey: 'dateRangeFilter.lastMonth',
  },
  [DataRangeFilterEnum.Trailing3Months]: {
    label: 'Last 3 Months',
    translationKey: 'dateRangeFilter.last3Months',
  },
  [DataRangeFilterEnum.Trailing6Months]: {
    label: 'Last 6 Months',
    translationKey: 'dateRangeFilter.last6Months',
  },
  [DataRangeFilterEnum.Trailing9Months]: {
    label: 'Last 9 Months',
    translationKey: 'dateRangeFilter.last9Months',
  },
  [DataRangeFilterEnum.Trailing1Year]: {
    label: 'Last 12 Months',
    translationKey: 'dateRangeFilter.last12Months',
  },
  [DataRangeFilterEnum.OpenYear]: {
    label: 'Current Year',
    translationKey: 'dateRangeFilter.currentYear',
  },
  [DataRangeFilterEnum.Trailing2Years]: {
    label: 'Last 2 Years',
    translationKey: 'dateRangeFilter.last2Years',
  },
  [DataRangeFilterEnum.AllTime]: {
    label: 'All Time',
    translationKey: 'dateRangeFilter.allTime',
  },
};

export function advisorFeedDateTranslationKey(
  filter: DataRangeFilterEnum,
): string {
  const key = ADVISOR_FEED_DATE_FILTER_META[filter]?.translationKey;
  if (key === undefined) {
    throw new Error(`Unsupported advisor feed date filter: ${filter}`);
  }
  return key;
}

export function advisorFeedDateFilterLabel(
  filter: DataRangeFilterEnum,
): string {
  const label = ADVISOR_FEED_DATE_FILTER_META[filter]?.label;
  if (label === undefined) {
    throw new Error(`Unsupported advisor feed date filter: ${filter}`);
  }
  return label;
}

export const advisorFeedDateFilterOptions: DateRangeFilterOption[] =
  statementsVaultDateFilters.map((filter) => ({
    filter,
    label: advisorFeedDateFilterLabel(filter),
  }));

/**
 * Tax Documents2 stores a year interval for category 2. Month presets send -1,
 * the narrowest year code that still contains the statement-date window.
 */
export function toAdvisorTaxDateIntervalApiValue(
  filter: DataRangeFilterEnum,
): string {
  switch (filter) {
    case DataRangeFilterEnum.Trailing1Month:
    case DataRangeFilterEnum.Trailing3Months:
    case DataRangeFilterEnum.Trailing6Months:
    case DataRangeFilterEnum.Trailing9Months:
    case DataRangeFilterEnum.Trailing1Year:
    case DataRangeFilterEnum.OpenYear:
      return '-1';
    case DataRangeFilterEnum.Trailing2Years:
      return '-2';
    case DataRangeFilterEnum.AllTime:
      return '';
    default:
      throw new Error(`Unsupported advisor tax date filter: ${filter}`);
  }
}

function feedWindowStart(
  filter: DataRangeFilterEnum,
  today: dayjs.Dayjs,
): dayjs.Dayjs | null {
  switch (filter) {
    case DataRangeFilterEnum.Trailing1Month:
      return today.subtract(1, 'month');
    case DataRangeFilterEnum.Trailing3Months:
      return today.subtract(3, 'month');
    case DataRangeFilterEnum.Trailing6Months:
      return today.subtract(6, 'month');
    case DataRangeFilterEnum.Trailing9Months:
      return today.subtract(9, 'month');
    case DataRangeFilterEnum.Trailing1Year:
      return today.subtract(12, 'month');
    case DataRangeFilterEnum.Trailing2Years:
      return today.subtract(24, 'month');
    case DataRangeFilterEnum.OpenYear:
      return today.startOf('year');
    case DataRangeFilterEnum.AllTime:
      return null;
    default:
      throw new Error(`Unsupported advisor feed date filter: ${filter}`);
  }
}

export function isStatementDateInFeedWindow(
  statementDate: string | undefined | null,
  filter: DataRangeFilterEnum,
  today: Date = new Date(),
  uploadDate?: string | null,
): boolean {
  if (filter === DataRangeFilterEnum.AllTime) {
    return true;
  }

  let effectiveDate = statementDate;
  if (effectiveDate == null || effectiveDate === '') {
    if (uploadDate != null && uploadDate !== '') {
      effectiveDate = uploadDate;
    } else {
      return false;
    }
  }

  const statementDay = dayjs(effectiveDate);
  if (!statementDay.isValid()) {
    return false;
  }

  const todayDay = dayjs(today);
  const start = feedWindowStart(filter, todayDay);
  if (start == null) {
    return true;
  }

  return (
    !statementDay.startOf('day').isBefore(start.startOf('day'), 'day') &&
    !statementDay.startOf('day').isAfter(todayDay.startOf('day'), 'day')
  );
}

export function formatFeedStatementDate(
  statementDate: string | null | undefined,
  uploadDate?: string | null,
): string {
  const chosen =
    statementDate != null &&
    statementDate !== '' &&
    dayjs(statementDate).isValid()
      ? statementDate
      : uploadDate != null && uploadDate !== '' && dayjs(uploadDate).isValid()
        ? uploadDate
        : null;
  return chosen == null ? '' : dayjs(chosen).format('MM/DD/YYYY');
}

export function formatVaultAccountOption(account: {
  Name: string;
  AccountNumber: string;
}): string {
  return `${account.Name} (${account.AccountNumber})`;
}

export function filterFeedRowsByAccounts<
  T extends { WealthAccountId?: number | null },
>(rows: readonly T[], selectedAccountIds: readonly number[]): T[] {
  if (selectedAccountIds.length === 0) {
    return [...rows];
  }

  const selected = new Set(selectedAccountIds);
  return rows.filter(
    (row) => row.WealthAccountId != null && selected.has(row.WealthAccountId),
  );
}

export function filterFeedRowsByEntitledAccounts<
  T extends { WealthAccountId?: number | null },
>(rows: readonly T[], entitledAccounts: readonly VaultFileAccount[]): T[] {
  const entitled = new Set(
    entitledAccounts.map((account) => account.WealthAccountId),
  );

  return rows.filter(
    (row) => row.WealthAccountId != null && entitled.has(row.WealthAccountId),
  );
}

export function advisorFeedDocuments2WealthAccountId(options: {
  isAccountView: boolean;
  wealthAccountId?: number;
  selectedAccount: string;
  selectedAccountIds: readonly number[];
}): string {
  const {
    isAccountView,
    wealthAccountId,
    selectedAccount,
    selectedAccountIds,
  } = options;

  if (isAccountView) {
    return String(wealthAccountId);
  }
  if (selectedAccountIds.length === 0 && selectedAccount !== '') {
    return selectedAccount;
  }
  return '';
}

export function applyAdvisorFeedRows<
  T extends {
    StatementDate?: string | null;
    WealthAccountId?: number | null;
  },
>(
  rows: readonly T[],
  dateFilter: DataRangeFilterEnum,
  selectedAccountIds: readonly number[],
  today: Date = new Date(),
): T[] {
  const dateFiltered = filterFeedRowsByStatementDate(rows, dateFilter, today);
  return filterFeedRowsByAccounts(dateFiltered, selectedAccountIds);
}

export function filterFeedRowsByStatementDate<
  T extends { StatementDate?: string | null; UploadDate?: string | null },
>(
  rows: readonly T[],
  filter: DataRangeFilterEnum,
  today: Date = new Date(),
): T[] {
  if (filter === DataRangeFilterEnum.AllTime) {
    return [...rows];
  }
  return rows.filter((row) =>
    isStatementDateInFeedWindow(
      row.StatementDate,
      filter,
      today,
      row.UploadDate,
    ),
  );
}
